import { useForm } from '@tanstack/react-form';

import { BlockReasonSchema, type BlockReason } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Button, Input } from '@web/ui/core/atoms';
import { DateTimePicker } from '@web/ui/core/molecules';
import { fireAndForget } from '@web/ui/core/utils';

/*
 * BlockReasonForm — lets the user pick one of 3 block reasons and emits a
 * validated BlockReason on submit.
 *
 * Uses a native <select> rather than a RadioGroup atom (no such atom exists)
 * or a Radix Select (overkill for 3 options and harder to drive with
 * @testing-library/user-event's selectOptions).
 */

type Kind = BlockReason['kind'];

interface FormValues {
  kind: Kind;
  source: 'client' | 'vendor';
  runAt: string;
  gate: string;
}

const DEFAULT_VALUES: FormValues = {
  kind: 'awaiting_input',
  source: 'client',
  runAt: '',
  gate: '',
};

function toBlockReason(v: FormValues): BlockReason | null {
  let candidate: unknown;
  if (v.kind === 'scheduled') {
    candidate = { kind: 'scheduled', runAt: v.runAt };
  } else if (v.kind === 'human_in_the_loop') {
    candidate = { kind: 'human_in_the_loop', gate: v.gate.trim() };
  } else {
    candidate = { kind: 'awaiting_input', source: v.source };
  }
  const parsed = BlockReasonSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

export function BlockReasonForm({
  onSubmit,
  pending,
}: Readonly<{ onSubmit: (reason: BlockReason) => void; pending: boolean }>): React.JSX.Element {
  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    onSubmit: ({ value }) => {
      const reason = toBlockReason(value);
      if (reason) onSubmit(reason);
    },
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        fireAndForget(form.handleSubmit());
      }}
    >
      <form.Field name="kind">
        {(field) => (
          <label className="flex flex-col gap-1 text-meta text-muted-foreground">
            {t('board.taskDrawer.block.kindLabel')}
            <select
              aria-label={t('board.taskDrawer.block.kindLabel')}
              className="h-8 rounded-md border border-border-soft bg-surface px-2 text-base text-foreground"
              value={field.state.value}
              onChange={(e) => {
                field.handleChange(e.target.value as Kind);
              }}
            >
              <option value="awaiting_input">
                {t('board.taskDrawer.block.kind.awaiting_input')}
              </option>
              <option value="scheduled">{t('board.taskDrawer.block.kind.scheduled')}</option>
              <option value="human_in_the_loop">
                {t('board.taskDrawer.block.kind.human_in_the_loop')}
              </option>
            </select>
          </label>
        )}
      </form.Field>
      <form.Subscribe selector={(s) => s.values.kind}>
        {(kind) => {
          if (kind === 'awaiting_input') {
            return (
              <form.Field name="source">
                {(field) => (
                  <label className="flex flex-col gap-1 text-meta text-muted-foreground">
                    {t('board.taskDrawer.block.sourceLabel')}
                    <select
                      aria-label={t('board.taskDrawer.block.sourceLabel')}
                      className="h-8 rounded-md border border-border-soft bg-surface px-2 text-base text-foreground"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value as FormValues['source']);
                      }}
                    >
                      <option value="client">{t('board.taskDrawer.block.source.client')}</option>
                      <option value="vendor">{t('board.taskDrawer.block.source.vendor')}</option>
                    </select>
                  </label>
                )}
              </form.Field>
            );
          }
          if (kind === 'scheduled') {
            return (
              <form.Field name="runAt">
                {(field) => (
                  <label className="flex flex-col gap-1 text-meta text-muted-foreground">
                    {t('board.taskDrawer.block.runAtLabel')}
                    <DateTimePicker
                      ariaLabel={t('board.taskDrawer.block.runAtLabel')}
                      placeholder={t('board.taskDrawer.block.runAtPlaceholder')}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  </label>
                )}
              </form.Field>
            );
          }
          return (
            <form.Field name="gate">
              {(field) => (
                <label className="flex flex-col gap-1 text-meta text-muted-foreground">
                  {t('board.taskDrawer.block.gateLabel')}
                  <Input
                    type="text"
                    maxLength={100}
                    aria-label={t('board.taskDrawer.block.gateLabel')}
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                  />
                </label>
              )}
            </form.Field>
          );
        }}
      </form.Subscribe>
      <Button type="submit" variant="primary" size="sm" disabled={pending} aria-disabled={pending}>
        {t('board.taskDrawer.block.submit')}
      </Button>
    </form>
  );
}
